import { useDeal } from '../../../hooks/useDeal';
import { formatDate } from '../../../lib/utils';

interface Props { dealId: string; }

export function CompsTab({ dealId }: Props) {
  const { data } = useDeal(dealId);

  const median = data.comps.length
    ? [...data.comps].sort((a, b) => a.pricePerNma - b.pricePerNma)[Math.floor(data.comps.length / 2)]
    : null;
  const min = data.comps.reduce((a, b) => Math.min(a, b.pricePerNma), Infinity);
  const max = data.comps.reduce((a, b) => Math.max(a, b.pricePerNma), 0);

  return (
    <>
      <div className="workspace-head">
        <div>
          <h2 className="workspace-title">Nearby <em>comps</em></h2>
          <div className="workspace-subtitle">
            {data.comps.length} comparable transactions · same operator / nearby sections
          </div>
        </div>
        <div className="workspace-actions">
          <button className="btn-ghost">Add comp</button>
          <button className="btn-primary">Pull more</button>
        </div>
      </div>

      {data.comps.length > 0 && (
        <div className="picture-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="picture-stat">
            <div className="picture-stat-label">Range</div>
            <div className="picture-stat-value">
              ${(min / 1000).toFixed(0)}k–<em>${(max / 1000).toFixed(0)}k</em>
              <span className="unit">/NMA</span>
            </div>
          </div>
          <div className="picture-stat">
            <div className="picture-stat-label">Median</div>
            <div className="picture-stat-value">
              <em>${(median?.pricePerNma ?? 0) / 1000}k</em>
              <span className="unit">/NMA</span>
            </div>
          </div>
          <div className="picture-stat">
            <div className="picture-stat-label">Total volume</div>
            <div className="picture-stat-value">
              <em>${(data.comps.reduce((a, b) => a + b.totalPrice, 0) / 1_000_000).toFixed(2)}M</em>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--bg-card-soft)', border: '1px solid var(--border)', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '90px 110px 1fr 70px 90px 90px',
          padding: '10px 18px', borderBottom: '1px solid var(--border)',
          fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-faint)',
          textTransform: 'uppercase', letterSpacing: '0.1em',
        }}>
          <span>Date</span>
          <span>TRS</span>
          <span>Buyer ← Seller</span>
          <span style={{ textAlign: 'right' }}>NMA</span>
          <span style={{ textAlign: 'right' }}>$/NMA</span>
          <span style={{ textAlign: 'right' }}>Total</span>
        </div>
        {data.comps.map(c => (
          <div key={c.id} style={{
            display: 'grid', gridTemplateColumns: '90px 110px 1fr 70px 90px 90px',
            padding: '12px 18px', borderBottom: '1px solid var(--border)',
            alignItems: 'center', fontSize: 12,
          }}>
            <span style={{ fontFamily: 'var(--mono)', color: 'var(--text-dim)' }}>{formatDate(c.date)}</span>
            <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', fontWeight: 600 }}>{c.trs}</span>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong style={{ fontWeight: 500 }}>{c.buyer}</strong>{' '}
              <span style={{ color: 'var(--text-faint)' }}>← {c.seller}</span>
            </span>
            <span style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{c.netAcres}</span>
            <span style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: 'var(--accent)' }}>
              ${(c.pricePerNma / 1000).toFixed(0)}k
            </span>
            <span style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>
              ${(c.totalPrice / 1000).toFixed(0)}k
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
